import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { styled } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Skeleton } from "~/components/Skeleton";
import { PolicyByUnitResponse } from "../types";
import { ChartTooltip } from "./ChartTooltip";

export interface PolicyByUnitProps {
  data?: PolicyByUnitResponse;
  isFetching: boolean;
}

const ChartContainer = styled("div", {
  width: "100%",
});

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  "& .recharts-cartesian-grid-horizontal, & .recharts-cartesian-grid-vertical": {
    stroke: "$neutral-200",
  },
  "& .recharts-cartesian-axis-tick-value": {
    fill: "$neutrals-dark",
    fontSize: "$xs",
  },
});

// `fill` is a raw SVG attribute on recharts elements and cannot resolve
// Stitches `$token` references — use literal hex values.
const LOWER_FARE_COLOR = "#0064c5";
const FIXED_VALUE_COLOR = "#00a878";
const LEAD_TIME_COLOR = "#f5a623";

// Each unit row stacks 3 bars; give it room so the bars read clearly.
const ROW_HEIGHT = 96;
const MIN_HEIGHT = 280;
const BAR_SIZE = 18;

const toAdherencePct = (rate: number): number => Number(((1 - (rate ?? 0)) * 100).toFixed(1));

export const PolicyByUnit = ({ data, isFetching }: PolicyByUnitProps) => {
  if (isFetching || !data) {
    return (
      <Card css={{ p: "$6" }}>
        <Skeleton variant="text-6" css={{ width: "40%" }} />
        <Skeleton css={{ width: "100%", height: "300px", mt: "$4" }} />
      </Card>
    );
  }

  if (data.units.length === 0) {
    return <EmptyState>Nenhum dado de aderência por unidade disponível para o período.</EmptyState>;
  }

  const chartData = data.units.map((unit) => ({
    name: unit.unitName,
    lowerFare: toAdherencePct(unit.lowerFare.rate),
    fixedValue: toAdherencePct(unit.fixedValue.rate),
    leadTime: toAdherencePct(unit.leadTime.rate),
  }));

  const chartHeight = Math.max(MIN_HEIGHT, chartData.length * ROW_HEIGHT);

  return (
    <Card css={{ p: "$6" }}>
      <ChartContainer css={{ height: `${chartHeight}px` }}>
        <StyledResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            barGap={4}
            barCategoryGap="24%"
            margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value: number) => `${value}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={180}
            />
            <Tooltip
              cursor={{ fill: "rgba(228, 228, 231, 0.4)" }}
              content={<ChartTooltip valueFormatter={(value) => `${value}%`} />}
            />
            <Legend />
            <Bar
              dataKey="lowerFare"
              name="Menor Tarifa"
              fill={LOWER_FARE_COLOR}
              barSize={BAR_SIZE}
            />
            <Bar
              dataKey="fixedValue"
              name="Valor Fixo"
              fill={FIXED_VALUE_COLOR}
              barSize={BAR_SIZE}
            />
            <Bar dataKey="leadTime" name="Antecedência" fill={LEAD_TIME_COLOR} barSize={BAR_SIZE} />
          </BarChart>
        </StyledResponsiveContainer>
      </ChartContainer>
    </Card>
  );
};

PolicyByUnit.displayName = "PolicyByUnit";
